/* Magnetic buttons — vanilla port of the agency site's <Magnetic> wrapper
 * (components/site/magnetic.tsx): within RANGE px of a button's center the
 * element translates toward the cursor at STRENGTH, smoothed by the same
 * spring (stiffness 220, damping 18, mass 0.6) so it feels weighted, not
 * slingshot. True buttons only — link-style buttons, nav items and cards
 * stay still (magnetic underlined text reads as jitter, not weight).
 *
 * Document-level delegation on purpose: Horizon's Section Rendering API
 * swaps whole DOM subtrees (cart updates, variant changes, the header
 * hydration), which would orphan per-element listeners. Fine pointers
 * only; prefers-reduced-motion opts out entirely; transform-only so the
 * theme's own button styles are untouched at rest.
 */
(() => {
  'use strict';

  if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var SEL = '.button:not(.button-unstyled), .button-secondary';
  var RANGE = 80;       // px from center where the pull applies
  var STRENGTH = 0.4;   // site's CTA polish setting
  var MAX = 10;         // travel clamp, px
  var STIFF = 220;
  var DAMP = 18;
  var MASS = 0.6;

  var active = new Map();
  var raf = 0;
  var lastT = 0;
  var px = -1e4;
  var py = -1e4;
  var hovered = null;

  function tick(t) {
    var dt = Math.min((t - lastT) / 1000 || 1 / 60, 1 / 30);
    lastT = t;
    active.forEach(function (s, el) {
      if (!el.isConnected) {
        active.delete(el);
        return;
      }
      s.vx += ((s.tx - s.x) * STIFF - s.vx * DAMP) * (dt / MASS);
      s.vy += ((s.ty - s.y) * STIFF - s.vy * DAMP) * (dt / MASS);
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      if (!s.engaged && Math.abs(s.x) < 0.15 && Math.abs(s.y) < 0.15 && Math.abs(s.vx) < 1 && Math.abs(s.vy) < 1) {
        el.style.transform = '';
        el.style.willChange = '';
        active.delete(el);
        return;
      }
      el.style.transform = 'translate3d(' + s.x.toFixed(2) + 'px, ' + s.y.toFixed(2) + 'px, 0)';
    });
    raf = active.size ? requestAnimationFrame(tick) : 0;
  }

  function ensureLoop() {
    if (!raf) {
      lastT = performance.now();
      raf = requestAnimationFrame(tick);
    }
  }

  function evaluate() {
    if (hovered && !active.has(hovered)) {
      active.set(hovered, { x: 0, y: 0, vx: 0, vy: 0, tx: 0, ty: 0, engaged: true });
      hovered.style.willChange = 'transform';
    }
    active.forEach(function (s, el) {
      if (el !== hovered) {
        s.tx = 0;
        s.ty = 0;
        s.engaged = false;
        return;
      }
      var r = el.getBoundingClientRect();
      var dx = px - (r.left + r.width / 2);
      var dy = py - (r.top + r.height / 2);
      if (Math.hypot(dx, dy) > RANGE) {
        s.tx = 0;
        s.ty = 0;
      } else {
        s.tx = Math.max(-MAX, Math.min(MAX, dx * STRENGTH));
        s.ty = Math.max(-MAX, Math.min(MAX, dy * STRENGTH));
      }
      s.engaged = true;
    });
    if (active.size) ensureLoop();
  }

  document.addEventListener(
    'pointermove',
    function (e) {
      px = e.clientX;
      py = e.clientY;
      hovered = e.target instanceof Element ? e.target.closest(SEL) : null;
      // header chrome (localization trigger etc.) carries the .button class
      // but is navigation, not a CTA — never magnetize it. Same for the
      // footer's quiet secondary button (Retragere din contract): a legal
      // function, deliberately calm (overground-brand.css footer block).
      if (hovered && hovered.closest('#header-group, header')) hovered = null;
      if (hovered && hovered.matches('.button-secondary') && hovered.closest('footer')) hovered = null;
      evaluate();
    },
    { passive: true }
  );

  // pointer left the viewport straight off a button — release everything
  document.addEventListener('pointerleave', function () {
    hovered = null;
    evaluate();
  });

  // scrolling moves buttons under a resting cursor — re-aim from stored coords
  document.addEventListener(
    'scroll',
    function () {
      if (active.size) evaluate();
    },
    { passive: true, capture: true }
  );
})();
